import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { mutate as globalMutate } from 'swr';
import { format } from 'date-fns';
import { queryApi } from 'src/api/queryApi';
import { parseQueryNotes } from 'src/utils/queryNotesUtils';

const FIELD_LABELS = {
  dateWriting: 'Date writing',
  dueDate: 'Deadline',
  notes: 'Note',
};

const MODAL_TITLES = {
  dateWriting: 'Edit Date Writing',
  deadlineDate: 'Edit Deadline Date',
  deadlineTime: 'Edit Deadline Time',
  notes: 'Edit Note',
};

const INITIAL_MODAL_STATE = {
  open: false,
  queryId: null,
  field: null,
  title: '',
  value: '',
  originalDateTime: null,
};

const useQueryInlineEdit = (queries, mutate) => {
  const [editModal, setEditModal] = useState(INITIAL_MODAL_STATE);

  const handleSaveEdit = useCallback(
    async (queryId, field, value) => {
      const query = queries.find((q) => q.id === queryId);
      if (!query) throw new Error('Query not found');

      const notesData = parseQueryNotes(query.notes);
      const originalNotes = query.notes;
      const originalDueDate = query.dueDate;

      let updatedDueDate = query.dueDate;
      let updatedNotes = query.notes;

      if (field === 'dateWriting') {
        notesData.dateWriting = value ? value.toISOString() : null;
        updatedNotes = JSON.stringify(notesData);
      } else if (field === 'notes') {
        notesData.notes = value || '';
        updatedNotes = JSON.stringify(notesData);
      } else if (field === 'dueDate') {
        updatedDueDate = value;
      }

      const optimisticData = queries.map((q) =>
        q.id === queryId ? { ...q, notes: updatedNotes, dueDate: updatedDueDate } : q
      );

      try {
        await mutate(optimisticData, false);

        await queryApi.updateQueryV2({
          queryID: queryId,
          title: query.title,
          description: query.description,
          status: query.status,
          source: query.source,
          topics: query.topics,
          mediaOutlet: query.mediaOutlet,
          dueDate: updatedDueDate,
          notes: updatedNotes,
        });

        toast.success(`${FIELD_LABELS[field] || field} updated successfully`);

        await mutate();
        await globalMutate((key) => typeof key === 'string' && key.includes('/queries'));

        return true;
      } catch (error) {
        mutate(
          queries.map((q) => (q.id === queryId ? { ...q, notes: originalNotes, dueDate: originalDueDate } : q)),
          false
        );
        toast.error(`Failed to update ${(FIELD_LABELS[field] || field).toLowerCase()}`);
        throw error;
      }
    },
    [queries, mutate]
  );

  const handleOpenEditModal = useCallback((queryId, field, currentValue, originalDateTime = null) => {
    setEditModal({
      open: true,
      queryId,
      field,
      title: MODAL_TITLES[field] || 'Edit',
      value: currentValue || '',
      originalDateTime,
    });
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setEditModal(INITIAL_MODAL_STATE);
  }, []);

  const handleEditValueChange = useCallback((newValue) => {
    setEditModal((prev) => ({ ...prev, value: newValue }));
  }, []);

  const buildDueDate = useCallback((query, field, value) => {
    let dueDate = query.dueDate ? new Date(query.dueDate) : new Date();
    if (isNaN(dueDate.getTime())) dueDate = new Date();

    if (field === 'deadlineDate' && value) {
      const [year, month, day] = value.split('-').map(Number);
      dueDate.setFullYear(year, month - 1, day);
    } else if (field === 'deadlineTime' && value) {
      const [hours, minutes] = value.split(':').map(Number);
      dueDate.setHours(hours, minutes, 0, 0);
    }

    return format(dueDate, 'yyyy-MM-dd HH:mm:ss');
  }, []);

  const handleSaveFromModal = useCallback(async () => {
    const { queryId, field, value, originalDateTime } = editModal;
    handleCloseEditModal();

    try {
      if (field === 'deadlineDate' || field === 'deadlineTime') {
        const query = queries.find((q) => q.id === queryId);
        if (query) {
          await handleSaveEdit(queryId, 'dueDate', buildDueDate(query, field, value));
        }
        return;
      }

      if (field === 'dateWriting') {
        if (!value) {
          await handleSaveEdit(queryId, 'dateWriting', null);
          return;
        }
        // datetime-local gives "yyyy-MM-ddTHH:mm", date gives "yyyy-MM-dd"
        const hasTime = value.includes('T');
        const newDate = hasTime ? new Date(value) : new Date(value + 'T00:00:00');

        if (!hasTime) {
          if (originalDateTime) {
            const orig = new Date(originalDateTime);
            newDate.setHours(orig.getHours(), orig.getMinutes(), orig.getSeconds(), orig.getMilliseconds());
          } else {
            newDate.setHours(12, 0, 0, 0);
          }
        }

        await handleSaveEdit(queryId, 'dateWriting', newDate);
        return;
      }

      await handleSaveEdit(queryId, field, value);
    } catch (error) {
      // Handled in handleSaveEdit via toast
    }
  }, [editModal, queries, handleSaveEdit, handleCloseEditModal, buildDueDate]);

  return {
    editModal,
    handleEditValueChange,
    handleOpenEditModal,
    handleCloseEditModal,
    handleSaveFromModal,
    handleSaveEdit,
  };
};

export default useQueryInlineEdit;
